import type { z } from 'zod'
import { tarslogs, tarslogsEnabled } from '@/plugins/tarslogs'

interface ApiErrorResponse {
  error?: string
  message?: string
  existingReportId?: string
  appraisalYear?: number
}

export class ApiError extends Error {
  status: number
  existingReportId?: string
  appraisalYear?: number

  constructor(message: string, status: number, data?: ApiErrorResponse) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.existingReportId = data?.existingReportId
    this.appraisalYear = data?.appraisalYear
  }
}

export function useApi() {
  const baseUrl = import.meta.env.VITE_API_URL || '/api'

  async function request<T>(
    path: string,
    options?: RequestInit,
    schema?: z.ZodSchema<T>
  ): Promise<T> {
    const url = `${baseUrl}${path}`

    const res = await fetch(url, {
      ...options,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers
      }
    })

    const data = await res.json()

    if (!res.ok) {
      const apiErrorData = data as ApiErrorResponse
      const errorMsg = apiErrorData.error || apiErrorData.message || `Request failed: ${res.status}`
      if (tarslogsEnabled && res.status >= 500) {
        tarslogs.captureError(new Error(errorMsg), {
          source: 'api',
          method: options?.method || 'GET',
          path,
          status: res.status,
        })
      }
      throw new ApiError(errorMsg, res.status, apiErrorData)
    }

    return schema ? schema.parse(data) : (data as T)
  }

  async function requestFormData<T>(
    path: string,
    formData: FormData,
    schema?: z.ZodSchema<T>
  ): Promise<T> {
    const url = `${baseUrl}${path}`

    const res = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      body: formData,
      // Don't set Content-Type - browser will set it with boundary for multipart
    })

    const data = await res.json()

    if (!res.ok) {
      const apiErrorData = data as ApiErrorResponse
      const errorMsg = apiErrorData.error || apiErrorData.message || `Request failed: ${res.status}`
      if (tarslogsEnabled && res.status >= 500) {
        tarslogs.captureError(new Error(errorMsg), {
          source: 'api',
          method: 'POST',
          path,
          status: res.status,
        })
      }
      throw new ApiError(errorMsg, res.status, apiErrorData)
    }

    return schema ? schema.parse(data) : (data as T)
  }

  return {
    get: <T>(path: string, schema?: z.ZodSchema<T>) =>
      request<T>(path, { method: 'GET' }, schema),

    post: <T>(path: string, body: unknown, schema?: z.ZodSchema<T>) =>
      request<T>(path, { method: 'POST', body: JSON.stringify(body) }, schema),

    postFormData: <T>(path: string, formData: FormData, schema?: z.ZodSchema<T>) =>
      requestFormData<T>(path, formData, schema),

    put: <T>(path: string, body: unknown, schema?: z.ZodSchema<T>) =>
      request<T>(path, { method: 'PUT', body: JSON.stringify(body) }, schema),

    patch: <T>(path: string, body: unknown, schema?: z.ZodSchema<T>) =>
      request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }, schema),

    delete: <T>(path: string, schema?: z.ZodSchema<T>) =>
      request<T>(path, { method: 'DELETE' }, schema),

    getUrl: (path: string) => `${baseUrl}${path}`,
  }
}
